import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { FiTooltipBase } from '@fi-shared/fi-tooltip/fi-tooltip-base';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { findClosest } from '@fi-shared/utils';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';

@Component({
    selector: 'fi-tooltip',
    templateUrl: './fi-tooltip.component.html',
    styleUrls: ['./fi-tooltip.component.scss', './fi-tooltip-content.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-tooltip' },
    standalone: false,
})
export class FiTooltipComponent extends FiTooltipBase implements OnInit, AfterViewInit, OnDestroy {
    @Input() showTooltipOnHover = true;

    ngOnInit() {
        super.ngOnInit();
        this.setServices();
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();

        if (this.showTooltipOnHover) {
            const contentElement = this.getContentElement();
            fromEvent(contentElement, 'mouseenter')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(($event: MouseEvent) => this.showTooltip());

            fromEvent(contentElement, 'mouseleave')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(($event: MouseEvent) => {
                    this.hideTooltip();
                });
        }
    }

    protected setTooltipVisibility(visible: boolean) {
        super.setTooltipVisibility(visible);
        if (visible) {
            setTimeout(() => this.setEventsToTooltipBox());
        }
    }

    private setEventsToTooltipBox() {
        if (this.tooltipRef.componentRef) {
            this.removeMouseListeners();
            const tooltipElement = this.tooltipRef.componentRef.instance.elementRef.nativeElement;
            const mouseEnterTooltipSubscription = fromEvent(tooltipElement, 'mouseenter')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(($event: MouseEvent) => this.tooltipRef.show());

            const mouseLeaveTooltipSubscription = fromEvent(tooltipElement, 'mouseleave')
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(($event: MouseEvent) => {
                    this.hideTooltip();
                });
            this.mouseListeners.push(mouseEnterTooltipSubscription, mouseLeaveTooltipSubscription);
        }
    }

    removeMouseListeners() {
        this.mouseListeners.forEach((subscription) => subscription.unsubscribe());
        this.mouseListeners.length = 0;
    }

    mouseListeners: Subscription[] = [];
    ngOnDestroy() {
        this.removeMouseListeners();
        super.ngOnDestroy();
    }

    private browserEventsService: FiBrowserEventsService;
    protected setServices() {
        super.setServices();
        this.browserEventsService = this.injector.get(FiBrowserEventsService);
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
